import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import noLogo from "../../../../assets/img/brand/noLogo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * =========================================================
 * Reporte de Cierre Mensual (estructura EXACTA de la imagen)
 * - Tabla "Cierres Diarios": SIEMPRE 1..31 (como plantilla)
 * - Tablas fiscales: dinámicas; si vienen vacías => data ficticia
 * - Estilo simple/lineal (igual a tu plantilla)
 * =========================================================
 */
const monthlyClosingReport = ({
  getDaysOfMonth = [],
  centerName,
  doctorName,
  date,
  fiscal = {},
  logoDataUrl,
}) => {
  // ---------------------------
  // Helpers
  // ---------------------------
  const safeText = (v, fallback = "—") => {
    const s = (v ?? "").toString().trim();
    return s.length ? s : fallback;
  };

  const toNum = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const money = (v) =>
    Math.max(0, Math.round((toNum(v, 0) + Number.EPSILON) * 100) / 100);

  const hasDailyClosingValues = (row = {}) =>
    money(row.insuredCoverage) > 0 ||
    money(row.totalSales) > 0 ||
    money(row.consultoryFee) > 0;

  const dailyClosingsWithValues = getDaysOfMonth.filter(hasDailyClosingValues);

  const fmtMoney = (() => {
    const nf = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    return (v) => nf.format(money(v));
  })();

  const formatDate = (d) => {
    const m = moment(d || new Date());
    return m.isValid() ? m.format("DD/MM/YYYY") : moment().format("DD/MM/YYYY");
  };

  // ---------------------------
  // Data: cierres diarios (map por día)
  // data.dailyClosings: [{ day: 1..31, insured: number, copay: number, total?: number }]
  // ---------------------------
  const totalInsured = money(
    dailyClosingsWithValues.reduce((a, r) => a + parseFloat(r.insuredCoverage || 0), 0),
  );
  const totalCopay = money(
    dailyClosingsWithValues.reduce((a, r) => a + parseFloat(r.totalSales || 0), 0),
  );
  const totalMonth = money(
    dailyClosingsWithValues.reduce((a, r) => a + parseFloat(r.consultoryFee || 0), 0),
  );

  // ---------------------------
  // Data: comprobantes fiscales
  // ---------------------------
  const insuredNCFRaw = Array.isArray(fiscal?.insuredNCF)
    ? fiscal.insuredNCF
    : [];
  const copayNCFRaw = Array.isArray(fiscal?.copayNCF)
    ? fiscal.copayNCF
    : [];
  const otherNCFRaw = Array.isArray(fiscal?.otherNCF)
    ? fiscal.otherNCF
    : [];

  const insuredNCF = insuredNCFRaw;
  const copayNCF = copayNCFRaw;
  const otherNCF = otherNCFRaw;

  const insuredNCFSubtotal = money(
    insuredNCF.reduce((a, r) => a + money(r?.value), 0),
  );
  const copayNCFSubtotal = money(
    copayNCF.reduce((a, r) => a + money(r?.value), 0),
  );
  const otherNCFSubtotal = money(
    otherNCF.reduce((a, r) => a + money(r?.value), 0),
  );

  // ---------------------------
  // Layouts
  // ---------------------------
  const layoutGrid = {
    hLineWidth: () => 0.8,
    vLineWidth: () => 0.8,
    hLineColor: () => "#000",
    vLineColor: () => "#000",
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 3,
    paddingBottom: () => 3,
  };

  const layoutNoBorder = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  };

  const sectionTitle = (t) => ({
    text: t,
    style: "sectionTitle",
    margin: [0, 10, 0, 6],
  });

  // ---------------------------
  // Builders (bloques)
  // ---------------------------
  const makeLogoBlock = () => {
    if (logoDataUrl) return { image: logoDataUrl, width: 120 };
    return {
      layout: layoutNoBorder,
      width: 150,
      table: { widths: ["*"], body: [[{ image: noLogo, width: 150 }]] },
    };
  };

  const makeHeaderRight = () => ({
    stack: [
      {},
      {
        text: "Reporte de Cierre Mensual",
        style: "title",
        alignment: "right",
        fontSize: 14,
        margin: [0, 50, 0, 0],
      },
      {
        text: `Fecha: ${formatDate(date)}`,
        alignment: "right",
        margin: [0, 6, 0, 0],
        bold: true,
        fontSize: 10,
      },
    ],
  });

  const makeInfoBlock = () => ({
    table: {
      widths: ["50%", "50%"],
      body: [
        [
          {
            text: [
              { text: "Centro: ", bold: true, fontSize: 11 },
              {
                text: safeText(centerName, "Display"),
                fontSize: 11,
                decoration: "underline",
              },
            ],
          },
          {
            text: [
              { text: "Médico: ", bold: true, fontSize: 11 },
              {
                text: safeText(doctorName, "Display"),
                fontSize: 11,
                decoration: "underline",
              },
            ],
            alignment: "right",
          },
        ],
      ],
    },
    layout: layoutNoBorder,
    margin: [0, 8, 0, 0],
  });

  const makeDailyClosingsTable = () => {
    const header = [
      { text: "Día", style: "th", alignment: "center" },
      { text: "Ventas Seguro Médico", style: "th", alignment: "center" },
      { text: "Co-Pagos", style: "th", alignment: "center" },
      { text: "Total", style: "th", alignment: "center" },
    ];

    const rows = dailyClosingsWithValues.map((r) => [
      { text: moment(r?.date).format("DD/MM"), style: "td", alignment: "center" },
      { text: fmtMoney(r.insuredCoverage), style: "td", alignment: "right" },
      { text: fmtMoney(r.totalSales), style: "td", alignment: "right" },
      { text: fmtMoney(r.consultoryFee), style: "td", alignment: "right" },
    ]);

    const totalRow = [
      { text: "Total", bold: true, alignment: "center" },
      { text: fmtMoney(totalInsured), bold: true, alignment: "right" },
      { text: fmtMoney(totalCopay), bold: true, alignment: "right" },
      { text: fmtMoney(totalMonth), bold: true, alignment: "right" },
    ];

    return {
      table: {
        headerRows: 1,
        widths: [40, "*", "*", "*"],
        body: [header, ...rows, totalRow],
      },
      layout: layoutGrid,
      margin: [0, 0, 0, 8],
    };
  };

  const makeInsuredNCFTable = () => {
    const header = [
      { text: "Aseguradora", style: "th", alignment: "center" },
      { text: "Fecha¹", style: "th", alignment: "center" },
      { text: "NCF", style: "th", alignment: "center" },
      { text: "Valor", style: "th", alignment: "center" },
    ];

    const rows = insuredNCF.map((r) => [
      {
        stack: [
          { text: safeText(r?.insurerName) },
          ...((r?.insurerRnc || r?.rnc)
            ? [{ text: `RNC: ${safeText(r?.insurerRnc || r?.rnc)}` }]
            : []),
        ],
        style: "td",
      },
      {
        text: r?.date ? formatDate(r.date) : "—",
        style: "td",
        alignment: "center",
      },
      { text: safeText(r?.ncf), style: "td", alignment: "center" },
      { text: fmtMoney(r?.value), style: "td", alignment: "right" },
    ]);

    const subtotalRow = [
      { text: "Sub-Total", colSpan: 3, bold: true, alignment: "center" },
      {},
      {},
      { text: fmtMoney(insuredNCFSubtotal), bold: true, alignment: "right" },
    ];

    return {
      table: {
        headerRows: 1,
        widths: ["*", 85, 110, 80],
        body: [header, ...rows, subtotalRow],
      },
      layout: layoutGrid,
      margin: [0, 0, 0, 8],
    };
  };

  const makeCopayNCFTable = () => {
    const header = [
      { text: "Co-Pagos", style: "th", alignment: "center" },
      { text: "Fecha¹", style: "th", alignment: "center" },
      { text: "NCF", style: "th", alignment: "center" },
      { text: "Valor", style: "th", alignment: "center" },
    ];

    const rows = copayNCF.map((r) => [
      { text: safeText(r?.voucherName, "Consumidor Final"), style: "td" },
      {
        text: r?.date ? formatDate(r.date) : "—",
        style: "td",
        alignment: "center",
      },
      { text: safeText(r?.ncf), style: "td", alignment: "center" },
      { text: fmtMoney(r?.value), style: "td", alignment: "right" },
    ]);

    const subtotalRow = [
      { text: "Sub-Total", colSpan: 3, bold: true, alignment: "center" },
      {},
      {},
      { text: fmtMoney(copayNCFSubtotal), bold: true, alignment: "right" },
    ];

    return {
      table: {
        headerRows: 1,
        widths: ["*", 85, 110, 80],
        body: [header, ...rows, subtotalRow],
      },
      layout: layoutGrid,
      margin: [0, 0, 0, 8],
    };
  };

  const makeOtherNCFTable = () => {
    const header = [
      { text: "Cliente", style: "th", alignment: "center" },
      { text: "Fecha²", style: "th", alignment: "center" },
      { text: "NCF", style: "th", alignment: "center" },
      { text: "Valor", style: "th", alignment: "center" },
    ];

    const rows = otherNCF.map((r) => [
      { text: safeText(r?.clientName), style: "td" },
      {
        text: r?.date ? formatDate(r.date) : "—",
        style: "td",
        alignment: "center",
      },
      { text: safeText(r?.ncf), style: "td", alignment: "center" },
      { text: fmtMoney(r?.value), style: "td", alignment: "right" },
    ]);

    const subtotalRow = [
      { text: "Sub-Total", colSpan: 3, bold: true, alignment: "center" },
      {},
      {},
      { text: fmtMoney(otherNCFSubtotal), bold: true, alignment: "right" },
    ];

    return {
      table: {
        headerRows: 1,
        widths: ["*", 85, 110, 80],
        body: [header, ...rows, subtotalRow],
      },
      layout: layoutGrid,
      margin: [0, 0, 0, 8],
    };
  };

  const makeFiscalSection = () => ({
    stack: [
      // Solo el titulo queda unido a la primera tabla; las facturas con NCF pueden ocupar varias paginas.
      {
        unbreakable: true,
        stack: [sectionTitle("Comprobantes Fiscales"), makeInsuredNCFTable()],
      },
      makeCopayNCFTable(),
      { text: "Facturas con NCF:", margin: [0, 2, 0, 6] },
      makeOtherNCFTable(),
    ],
  });

  const makeSignature = () => ({
    stack: [
      {
        canvas: [
          { type: "line", x1: 160, y1: 0, x2: 360, y2: 0, lineWidth: 0.9 },
        ],
        margin: [0, 22, 0, 6],
      },
      {
        text: safeText(doctorName, "Nombre de Usuario"),
        alignment: "center",
        fontSize: 10,
      },
      {
        text: "Firma",
        alignment: "center",
        fontSize: 10,
        margin: [0, 4, 0, 0],
      },
    ],
    margin: [0, 10, 0, 0],
  });

  // ---------------------------
  // docDefinition
  // ---------------------------

  return {
    pageMargins: [30, 30, 30, 20],
    defaultStyle: { fontSize: 9 },
    styles: {
      title: { fontSize: 12, bold: true, decoration: "underline" },
      sectionTitle: { fontSize: 9, bold: true, decoration: "underline" },
      th: { bold: true, fontSize: 9 },
      td: { fontSize: 9 },
    },
    content: [
      // Header (Logo + Título)
      {
        columns: [
          { width: 170, ...makeLogoBlock() },
          { width: "*", ...makeHeaderRight() },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 6],
      },

      // Centro / Médico (con underline)
      makeInfoBlock(),

      // Sección: Cierres Diarios
      sectionTitle("Cierres Diarios"),
      makeDailyClosingsTable(),

      // Sección: Comprobantes Fiscales
      makeFiscalSection(),

      // Firma
      makeSignature(),
    ],
  };
};

export const openMonthlyClosingReport = (data) => {
  pdfMake.createPdf(monthlyClosingReport(data)).open();
};
